import { RecordingTypes } from '@carboncopyco/types';
import { Checkbox, ColorHex, TextBase } from '@carboncopyco/ui';
import { useEffect, useMemo } from 'react';
import { useImportManagerState } from '../ImportManagerContext';
import type { CategorizedData } from '../types';
import {
  categoryIconMap,
  categoryNameMap,
  categorySchemasMap,
  findCategoryId,
  getInfoIconProps,
} from '../utils';
import { InfoIconTooltip } from './InfoIconTooltip';

export const ModificationCategory = ({
  categoryId,
  count,
  schemaTypes,
  relatedSchemas,
}: CategorizedData) => {
  const {
    selectedSchemas,
    updateSelectedSchemas,
    recordingType,
    indeterminateSchemas,
    isGroupTypeSelected,
    setIndeterminateSchemas,
    modificationCategories,
  } = useImportManagerState();

  const Icon = categoryIconMap[categoryId];
  const categorySchemas = categorySchemasMap[categoryId];
  const isChecked = useMemo(
    () => selectedSchemas.some((schema) => schemaTypes.includes(schema)),
    [selectedSchemas, schemaTypes]
  );

  const isIndeterminate = useMemo(
    () => indeterminateSchemas.some((schema) => schemaTypes.includes(schema)),
    [indeterminateSchemas, schemaTypes]
  );

  const shouldAddIndeterminate =
    !isIndeterminate && isGroupTypeSelected && categoryId === 'findReplace';
  const isGroupType = categoryId === 'aiPromptGroup';

  const tooltipItem = useMemo(
    () =>
      getInfoIconProps(categoryId, recordingType, isChecked, isIndeterminate),
    [categoryId, recordingType, isChecked, isIndeterminate]
  );

  const isDisabled = useMemo(
    () => categoryId === 'edits' && recordingType === RecordingTypes.overlay,
    [categoryId, recordingType]
  );

  const getIndeterminateRelatedSchemas = () => {
    const relatedSchemasCategoryIds = new Set(
      relatedSchemas.map((schema) => findCategoryId(schema))
    );

    const uniqueRelatedSchemas = Array.from(
      new Set(
        modificationCategories
          .filter(({ categoryId }) => relatedSchemasCategoryIds.has(categoryId))
          .flatMap(({ schemaTypes }) => schemaTypes)
      )
    );

    return uniqueRelatedSchemas;
  };

  useEffect(() => {
    if (isChecked && relatedSchemas) {
      const unselectedRelatedSchemas = relatedSchemas.filter(
        (schema) => !selectedSchemas.includes(schema)
      );
      // Add related schemas to selected schemas if they are not already selected and make them indeterminate
      if (unselectedRelatedSchemas.length > 0) {
        const indeterminateRelatedSchemas = getIndeterminateRelatedSchemas();
        updateSelectedSchemas((prev) => [
          ...prev,
          ...unselectedRelatedSchemas.filter(
            (schema) => !prev.includes(schema)
          ),
        ]);
        if (indeterminateRelatedSchemas.length > 0)
          setIndeterminateSchemas((prev) => [
            ...prev,
            ...indeterminateRelatedSchemas.filter(
              (schema) => !prev.includes(schema)
            ),
          ]);
      }
    }
  }, [relatedSchemas, isChecked, selectedSchemas]);

  const handleCheckboxChange = () => {
    if (isChecked && !isIndeterminate) {
      handleUncheckedState();
    } else {
      handleCheckedState();
    }
  };

  const handleUncheckedState = () => {
    if (shouldAddIndeterminate) {
      setIndeterminateSchemas((prev) => [
        ...prev,
        ...schemaTypes.filter((schema) => !prev.includes(schema)),
      ]);
    } else {
      updateSchemasAfterUncheck();
    }
  };

  const removeRelatedIndeterminateSchemas = () => {
    setIndeterminateSchemas((prev) =>
      prev.filter((schema) => !categorySchemas.includes(schema))
    );
  };

  const handleCheckedState = () => {
    if (isIndeterminate) {
      removeRelatedIndeterminateSchemas();
    }
    updateSelectedSchemas([
      ...selectedSchemas,
      ...schemaTypes.filter((schema) => !selectedSchemas.includes(schema)),
    ]);
  };

  const updateSchemasAfterUncheck = () => {
    if (isGroupType) {
      // Remove all schemas that are not part of the group type
      updateSelectedSchemas(
        selectedSchemas.filter(
          (schema) =>
            !indeterminateSchemas.includes(schema) &&
            !schemaTypes.includes(schema)
        )
      );
      removeRelatedIndeterminateSchemas();
    } else {
      updateSelectedSchemas(
        selectedSchemas.filter((schema) => !schemaTypes.includes(schema))
      );
    }
  };

  return (
    <li
      className="flex items-center p-3.5 gap-3 bg-ds-accent3-100 rounded-lg border border-ds-natural-150"
      key={categoryId}
    >
      <Checkbox
        indeterminate={isIndeterminate}
        checked={!isDisabled && isChecked}
        onChange={handleCheckboxChange}
        name={categoryId}
        key={categoryId}
        disabled={isDisabled}
      />
      <div className="flex gap-1.5">
        <Icon
          size={20}
          color={isDisabled ? ColorHex.natural200 : ColorHex.natural600}
        />
        <TextBase
          color={isDisabled ? 'text-ds-natural-200' : 'text-ds-natural-600'}
          label={categoryNameMap[categoryId]}
        />
        <TextBase
          color={isDisabled ? 'text-ds-natural-200' : 'text-ds-natural-600'}
          label={`(${count})`}
        />
      </div>
      <div className="ml-auto">
        {tooltipItem && <InfoIconTooltip {...tooltipItem} />}
      </div>
    </li>
  );
};
